<script setup>
import { ref, computed, watch } from 'vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import Spinner from '@/components/ui/Spinner.vue'
import CaptionService from '@/services/CaptionService'
import { summaryLabel } from './captionRecognition'

const open = defineModel({ type: Boolean, default: false })

const props = defineProps({
	flowId: { type: String, required: true },
	// What the editor holds now; decides whether Merge/Replace is asked.
	currentVocabCount: { type: Number, default: 0 },
	currentReplacementsCount: { type: Number, default: 0 },
})

const emit = defineEmits(['copy'])

const loading = ref(false)
const loadError = ref('')
const flows = ref([])
const selected = ref('')
const mode = ref('merge')
const copying = ref(false)
const copyError = ref('')

const hasCurrent = computed(() => props.currentVocabCount + props.currentReplacementsCount > 0)
const candidates = computed(() => flows.value.filter((f) => f.id !== props.flowId && f.settingsSummary?.hasSettings))
const selectedFlow = computed(() => candidates.value.find((f) => f.id === selected.value) || null)

async function load() {
	loading.value = true
	loadError.value = ''
	try {
		const res = await CaptionService.listFlows()
		flows.value = res.flows
		if (candidates.value.length === 1) selected.value = candidates.value[0].id
	} catch (err) {
		loadError.value = err?.message || 'Could not load your streams.'
	} finally {
		loading.value = false
	}
}

watch(open, (isOpen) => {
	if (!isOpen) return
	selected.value = ''
	mode.value = 'merge'
	copyError.value = ''
	load()
})

async function copy() {
	if (!selectedFlow.value || copying.value) return
	copying.value = true
	copyError.value = ''
	try {
		const res = await CaptionService.getFlow(selectedFlow.value.id)
		emit('copy', {
			source: selectedFlow.value,
			settings: res.flow?.settings || {},
			mode: hasCurrent.value ? mode.value : 'replace',
		})
		open.value = false
	} catch (err) {
		copyError.value = err?.message || 'Could not load that stream\'s settings.'
	} finally {
		copying.value = false
	}
}
</script>

<template>
	<ModalBox v-model="open" size="sm" title="Copy from another stream" scroll-body mobile-placement="bottom" body-classes="px-4 sm:px-6">
		<div class="flex flex-col gap-4 pb-4" data-testid="copy-settings">
			<p class="m-0 -mt-2 text-tiny text-surface-7">Start from a dictionary you've already built. You can review everything before saving.</p>

			<div v-if="loading" class="flex justify-center py-6">
				<Spinner text="Loading streams…" classes="text-dark-8" spinner-color="var(--c-dark-8)" spinner-size="18px" />
			</div>
			<div v-else-if="loadError" class="flex flex-col items-center gap-2 py-4 text-center">
				<p class="m-0 text-tiny text-error-light">{{ loadError }}</p>
				<FormButton type="secondary" size="sm" @click="load">Try again</FormButton>
			</div>
			<p v-else-if="!candidates.length" class="m-0 rounded-lg bg-surface-3/60 px-4 py-6 text-center text-tiny text-surface-8" data-testid="copy-empty">
				None of your other captioned streams have a dictionary or replacements yet.
			</p>
			<fieldset v-else class="m-0 border-0 p-0">
				<legend class="sr-only">Stream to copy from</legend>
				<ul class="m-0 flex max-h-72 list-none flex-col gap-1.5 overflow-y-auto p-0" data-testid="copy-candidates">
					<li v-for="f in candidates" :key="f.id">
						<label
							class="m-0 flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 ring-1 transition-colors"
							:class="selected === f.id ? 'bg-main/10 ring-main' : 'bg-surface-3/50 ring-transparent hover:bg-surface-3'"
						>
							<input v-model="selected" type="radio" name="copy-source" :value="f.id" class="size-4 shrink-0 accent-[#3E64FF]">
							<span class="flex min-w-0 flex-col">
								<span class="truncate text-tiny font-medium text-surface-text-2">{{ f.streamName || 'Untitled stream' }}</span>
								<span class="text-[11px] text-surface-7">{{ summaryLabel(f.settingsSummary) }}</span>
							</span>
						</label>
					</li>
				</ul>
			</fieldset>

			<div v-if="candidates.length && hasCurrent" class="flex flex-col gap-2">
				<span class="text-tiny font-medium text-surface-text-1">This stream already has {{ summaryLabel({ vocabCount: currentVocabCount, replacementsCount: currentReplacementsCount }) }}</span>
				<TabGroup type="segmented" cols="2" data-testid="copy-mode">
					<TabItem :is-active="mode === 'merge'" @click="mode = 'merge'">Merge</TabItem>
					<TabItem :is-active="mode === 'replace'" @click="mode = 'replace'">Replace</TabItem>
				</TabGroup>
				<p class="m-0 text-xs text-surface-7" data-testid="copy-mode-copy">
					<template v-if="mode === 'merge'">Adds its terms and replacements to yours. Your options stay as they are.</template>
					<template v-else>Uses its dictionary, replacements and options instead of yours.</template>
				</p>
			</div>

			<p v-if="copyError" class="m-0 text-xs text-error-light">{{ copyError }}</p>
		</div>

		<template #footer>
			<div class="flex flex-col-reverse gap-2 border-t border-surface-3 p-4 sm:flex-row sm:justify-end sm:px-6">
				<FormButton type="secondary" size="md" class="justify-center" @click="open = false">Cancel</FormButton>
				<FormButton type="main" size="md" class="justify-center" :disabled="!selectedFlow" :is-loading="copying" data-testid="copy-confirm" @click="copy">
					Copy settings
				</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
